import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Copy, Pencil, Code2, ExternalLink, Inbox, Palette, Pin, Trash2, Sparkles, Search, X, Lock, QrCode, Download, Share2, Film } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LangSwitcher } from "@/components/LangSwitcher";
import { ShareImageDialog } from "@/components/ShareImageDialog";
import { ReelsShareDialog } from "@/components/ReelsShareDialog";

const SWATCHES = ["#FF4F00", "#0A0A0A", "#2563EB", "#059669", "#DC2626", "#7C3AED"];
const WIDGET_DEFAULTS = { accent: "#FF4F00", theme: "light", layout: "masonry", order: "recent" };
export const defaultWaMessage = () =>
  "Oi! Adorei ter trabalhado com você. Pode gravar um depoimento rapidinho? Leva 1 minuto, é só abrir esse link:";

const Avatar = ({ name, hasAvatar, testimonialId }) => (
  hasAvatar ? (
    <img
      src={`${API}/testimonials/${testimonialId}/avatar`}
      alt={name}
      data-testid={`avatar-img-${testimonialId}`}
      className="w-9 h-9 rounded-full object-cover shrink-0"
    />
  ) : (
    <div className="w-9 h-9 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center text-sm font-bold shrink-0">
      {name?.[0]?.toUpperCase()}
    </div>
  )
);

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const en = !i18n.language?.startsWith("pt");
  const [campaign, setCampaign] = useState(null);
  const [testimonials, setTestimonials] = useState(null);
  const [showEmbed, setShowEmbed] = useState(false);
  const [widget, setWidget] = useState(WIDGET_DEFAULTS);
  const [savingWidget, setSavingWidget] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRating, setFilterRating] = useState(0);
  const [search, setSearch] = useState("");
  const [lastSeen, setLastSeen] = useState(null);
  const [waMessage, setWaMessage] = useState("");
  const [waOpen, setWaOpen] = useState(false);
  const [savingWa, setSavingWa] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCampaignOpen, setDeleteCampaignOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [transcribingId, setTranscribingId] = useState(null);
  const [editingTranscriptId, setEditingTranscriptId] = useState(null);
  const [transcriptDraft, setTranscriptDraft] = useState("");
  const [savingTranscript, setSavingTranscript] = useState(false);
  const [highlightingId, setHighlightingId] = useState(null);
  const [renderingReel, setRenderingReel] = useState(null);
  const [reelsShare, setReelsShare] = useState(null);

  useEffect(() => {
    api.get(`/campaigns/${id}`).then((r) => {
      setCampaign(r.data);
      setWidget({ ...WIDGET_DEFAULTS, ...(r.data.widget_settings || {}) });
      setLastSeen(r.data.last_seen_at || r.data.created_at);
      setWaMessage(r.data.whatsapp_message || defaultWaMessage(r.data.business_name));
      api.post(`/campaigns/${id}/seen`).catch(() => {});
    });
    api.get(`/campaigns/${id}/testimonials`).then((r) => setTestimonials(r.data));
  }, [id]);

  const saveWaMessage = async () => {
    setSavingWa(true);
    try {
      await api.patch(`/campaigns/${id}/whatsapp`, { message: waMessage });
      toast.success("Mensagem salva!");
    } catch {
      toast.error("Erro ao salvar mensagem");
    } finally {
      setSavingWa(false);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${waMessage.trim()} ${collectUrl}`)}`, "_blank", "noopener");
  };

  const transcribe = async (t) => {
    setTranscribingId(t.id);
    try {
      const r = await api.post(`/testimonials/${t.id}/transcribe`);
      setTestimonials((prev) => prev.map((x) => (x.id === t.id ? { ...x, transcript: r.data.transcript, transcript_status: "done" } : x)));
      toast.success("Transcrição gerada!");
    } catch {
      toast.error("Erro ao transcrever. Tente novamente.");
    } finally {
      setTranscribingId(null);
    }
  };

  const generateHighlight = async (t) => {
    setHighlightingId(t.id);
    try {
      const r = await api.post(`/testimonials/${t.id}/highlight`);
      setTestimonials((prev) => prev.map((x) => (x.id === t.id ? { ...x, highlight: r.data.highlight } : x)));
      toast.success("Frase de destaque gerada!");
    } catch {
      toast.error("Erro ao gerar destaque. Tente novamente.");
    } finally {
      setHighlightingId(null);
    }
  };

  const saveTranscript = async (t) => {
    setSavingTranscript(true);
    try {
      const r = await api.patch(`/testimonials/${t.id}/transcript`, { transcript: transcriptDraft });
      setTestimonials((prev) => prev.map((x) => (x.id === t.id ? { ...x, transcript: r.data.transcript, transcript_status: "done" } : x)));
      setEditingTranscriptId(null);
      toast.success("Transcrição atualizada!");
    } catch {
      toast.error("Erro ao salvar transcrição");
    } finally {
      setSavingTranscript(false);
    }
  };

  const confirmDeleteTestimonial = async () => {
    const t = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/testimonials/${t.id}`);
      setTestimonials((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Depoimento excluído");
    } catch {
      toast.error("Erro ao excluir depoimento");
    }
  };

  const confirmDeleteCampaign = async () => {
    setDeleteCampaignOpen(false);
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campanha excluída");
      navigate("/dashboard");
    } catch {
      toast.error("Erro ao excluir campanha");
    }
  };

  const saveWidget = async () => {
    setSavingWidget(true);
    try {
      await api.patch(`/campaigns/${id}/widget`, widget);
      toast.success("Aparência do mural salva!");
      setPreviewKey((k) => k + 1);
    } catch {
      toast.error("Erro ao salvar aparência");
    } finally {
      setSavingWidget(false);
    }
  };

  const collectUrl = campaign ? `${window.location.origin}/c/${campaign.slug}` : "";
  const embedCode = campaign
    ? `<div data-depoia="${campaign.slug}"></div>\n<script src="${window.location.origin}/embed.js" async><\/script>`
    : "";
  const iframeCode = campaign
    ? `<iframe src="${window.location.origin}/w/${campaign.slug}" style="width:100%;min-height:600px;border:none;" loading="lazy" title="Depoimentos"></iframe>`
    : "";

  const copy = (text, msg) => {
    navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  const togglePin = async (t) => {
    const pinned = !t.pinned;
    try {
      await api.patch(`/testimonials/${t.id}/pin`, { pinned });
      setTestimonials((prev) =>
        prev
          .map((x) => ({ ...x, pinned: x.id === t.id ? pinned : false }))
          .sort((a, b) => (b.pinned === a.pinned ? new Date(b.created_at) - new Date(a.created_at) : b.pinned ? 1 : -1))
      );
      toast.success(pinned ? "Depoimento fixado no topo do mural" : "Depoimento desfixado");
    } catch {
      toast.error("Erro ao fixar depoimento");
    }
  };

  const toggleApprove = async (t) => {
    if (t.locked) {
      toast.error("Limite do plano grátis atingido. Faça upgrade para aprovar este depoimento.");
      return;
    }
    const approved = !t.approved;
    setTestimonials((prev) => prev.map((x) => (x.id === t.id ? { ...x, approved } : x)));
    try {
      await api.patch(`/testimonials/${t.id}/approve`, { approved });
      toast.success(approved ? "Depoimento publicado no mural" : "Depoimento removido do mural");
    } catch {
      setTestimonials((prev) => prev.map((x) => (x.id === t.id ? { ...x, approved: !approved } : x)));
      toast.error("Erro ao atualizar");
    }
  };

  const downloadReels = async (ti) => {
    setRenderingReel(ti.id);
    const toastId = toast.loading(en ? "Rendering Reels MP4… (10-30s)" : "Renderizando Reels MP4… (10-30s)");
    try {
      const r = await api.get(`/testimonials/${ti.id}/reels.mp4`, { responseType: "blob", timeout: 180000 });
      const blob = new Blob([r.data], { type: "video/mp4" });
      const url = URL.createObjectURL(blob);
      const slug = (ti.author_name || "reel").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").slice(0, 40) || "reel";
      // Open the share dialog instead of auto-downloading
      setReelsShare({
        blob,
        url,
        filename: `${slug}-reels.mp4`,
        authorName: ti.author_name || "",
      });
      toast.success(en ? "Reels ready!" : "Reels pronto!", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (en ? "Failed to render video." : "Erro ao gerar o vídeo."), { id: toastId });
    } finally {
      setRenderingReel(null);
    }
  };

  if (!campaign) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-sm text-zinc-400">Carregando...</div>;

  const avgRating = testimonials && testimonials.length > 0
    ? testimonials.reduce((sum, t) => sum + (t.rating || 0), 0) / testimonials.length
    : 0;

  const filtered = (testimonials || []).filter(
    (t) =>
      (filterType === "all" || t.type === filterType) &&
      (filterStatus === "all" || (filterStatus === "approved" ? t.approved : !t.approved)) &&
      (filterRating === 0 || t.rating === filterRating) &&
      (!search.trim() ||
        t.author_name.toLowerCase().includes(search.trim().toLowerCase()) ||
        (t.author_role || "").toLowerCase().includes(search.trim().toLowerCase()))
  );
  const hasFilter = filterType !== "all" || filterStatus !== "all" || filterRating !== 0 || search.trim() !== "";

  const Chip = ({ active, onClick, children, testId }) => (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-zinc-200 text-zinc-600 bg-white hover:border-zinc-400"}`}
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" data-testid="back-to-dashboard-link" className="text-sm text-zinc-500 flex items-center gap-1.5 hover:text-zinc-800 transition-colors">
            <ArrowLeft size={15} /> {t("campaignDetail.back")}
          </Link>
          <LangSwitcher />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight" data-testid="campaign-title">{campaign.business_name}</h1>
            <p className="text-sm text-zinc-500 mt-1">{campaign.welcome_message || (en ? "No welcome message" : "Sem mensagem de boas-vindas")}</p>
            {testimonials && testimonials.length > 0 && (
              <div className="flex items-center gap-2 mt-3" data-testid="campaign-rating-summary">
                <StarRating value={Math.round(avgRating)} readOnly size={16} />
                <span className="font-heading font-extrabold text-lg" data-testid="campaign-avg-rating">
                  {avgRating.toFixed(1).replace(".", ",")}
                </span>
                <span className="text-sm text-zinc-500" data-testid="campaign-rating-count">
                  · {testimonials.length} {t("campaignDetail.ratings")}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="export-testimonials-btn"
              onClick={async () => {
                try {
                  const r = await api.get(`/campaigns/${id}/export`, { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url;
                  const disp = r.headers["content-disposition"] || "";
                  const m = disp.match(/filename="([^"]+)"/);
                  a.download = m ? m[1] : `testimonials-${campaign.slug}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success(t("campaignDetail.exportSuccess"));
                } catch {
                  toast.error(t("campaignDetail.exportError"));
                }
              }}
              className="px-4 py-2.5 border border-zinc-200 rounded-full text-sm font-semibold flex items-center gap-2 text-zinc-600 hover:border-zinc-400 transition-colors"
            >
              <Download size={14} /> {t("common.exportCsv")}
            </button>
            <Link data-testid="edit-campaign-btn" to={`/campaigns/${id}/edit`} className="px-4 py-2.5 border border-zinc-200 rounded-full text-sm font-semibold flex items-center gap-2 text-zinc-600 hover:border-zinc-400 transition-colors">
              <Pencil size={14} /> {t("common.edit")}
            </Link>
            <button
              data-testid="delete-campaign-btn"
              onClick={() => setDeleteCampaignOpen(true)}
              className="px-4 py-2.5 border border-red-200 rounded-full text-sm font-semibold flex items-center gap-2 text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 size={14} /> {t("common.delete")}
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          <div className="bg-white border border-zinc-200 rounded-lg p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3">{t("campaignDetail.collectLink")}</p>
            <p className="text-sm font-mono text-zinc-700 truncate mb-4" data-testid="collect-url">{collectUrl}</p>
            <div className="flex gap-2">
              <button data-testid="copy-collect-link-btn" onClick={() => copy(collectUrl, en ? "Link copied!" : "Link copiado!")} className="px-4 py-2 bg-[#FF4F00] text-white rounded-full text-xs font-semibold flex items-center gap-1.5 hover:bg-[#E64600] transition-colors">
                <Copy size={13} /> {t("common.copyLink")}
              </button>
              <button
                data-testid="whatsapp-share-btn"
                onClick={() => setWaOpen(true)}
                className="px-4 py-2 bg-[#25D366] text-white rounded-full text-xs font-semibold flex items-center gap-1.5 hover:bg-[#1EBE5A] transition-colors"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </button>
              <a data-testid="open-collect-link" href={collectUrl} target="_blank" rel="noreferrer" className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold flex items-center gap-1.5 text-zinc-600 hover:border-zinc-400 transition-colors">
                <ExternalLink size={13} /> {t("common.open")}
              </a>
              <button
                data-testid="qrcode-btn"
                onClick={() => setQrOpen(true)}
                className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold flex items-center gap-1.5 text-zinc-600 hover:border-zinc-400 transition-colors"
              >
                <QrCode size={13} /> QR code
              </button>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3">{t("campaignDetail.embedCode")}</p>
            <p className="text-sm text-zinc-600 mb-4">{t("campaignDetail.embedDesc")}</p>
            <div className="flex gap-2">
              <button data-testid="copy-embed-btn" onClick={() => copy(embedCode, t("campaignDetail.codeCopied"))} className="px-4 py-2 bg-[#0A0A0A] text-white rounded-full text-xs font-semibold flex items-center gap-1.5 hover:bg-zinc-800 transition-colors">
                <Code2 size={13} /> {t("common.copyCode")}
              </button>
              <button data-testid="toggle-embed-btn" onClick={() => setShowEmbed(!showEmbed)} className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold text-zinc-600 hover:border-zinc-400 transition-colors">
                {showEmbed ? t("common.close") : t("campaignDetail.seeCode")}
              </button>
              <a data-testid="preview-wall-link" href={`/w/${campaign.slug}`} target="_blank" rel="noreferrer" className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold flex items-center gap-1.5 text-zinc-600 hover:border-zinc-400 transition-colors">
                <ExternalLink size={13} /> {t("campaignDetail.seeWall")}
              </a>
            </div>
            {showEmbed && (
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">{t("campaignDetail.embedScriptLabel")}</p>
                  <pre data-testid="embed-code" className="p-4 bg-[#0A0A0A] text-zinc-200 rounded-md text-xs overflow-x-auto whitespace-pre-wrap break-all">{embedCode}</pre>
                  <p className="text-xs text-zinc-500 mt-2">{t("campaignDetail.embedScriptHint")}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">{t("campaignDetail.embedIframeLabel")}</p>
                    <button
                      data-testid="copy-iframe-btn"
                      onClick={() => copy(iframeCode, t("campaignDetail.codeCopied"))}
                      className="text-xs font-semibold text-zinc-600 underline underline-offset-4 hover:text-[#FF4F00] transition-colors"
                    >
                      {t("common.copyCode")}
                    </button>
                  </div>
                  <pre data-testid="embed-iframe-code" className="p-4 bg-zinc-100 text-zinc-600 rounded-md text-xs overflow-x-auto whitespace-pre-wrap break-all">{iframeCode}</pre>
                  <p className="text-xs text-zinc-500 mt-2">{t("campaignDetail.embedIframeHint")}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <Dialog open={qrOpen} onOpenChange={setQrOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="qrcode-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">{t("campaignDetail.qrTitle")}</DialogTitle>
              <DialogDescription>{t("campaignDetail.qrDesc")}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 py-2">
              <div id="qrcode-canvas-wrap" className="p-5 bg-white border border-zinc-200 rounded-xl">
                <QRCodeCanvas
                  id="qrcode-canvas"
                  value={collectUrl}
                  size={220}
                  level="H"
                  marginSize={2}
                  fgColor="#0A0A0A"
                  bgColor="#FFFFFF"
                />
              </div>
              <p className="text-[11px] font-mono text-zinc-500 text-center break-all px-3">{collectUrl}</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                data-testid="qrcode-copy-btn"
                onClick={() => copy(collectUrl, "Link copiado!")}
                className="px-4 py-2.5 border border-zinc-200 rounded-full text-xs font-semibold text-zinc-600 hover:border-zinc-400 transition-colors flex items-center gap-1.5"
              >
                <Copy size={13} /> Copiar link
              </button>
              <button
                data-testid="qrcode-download-btn"
                onClick={() => {
                  const canvas = document.getElementById("qrcode-canvas");
                  if (!canvas) return;
                  const url = canvas.toDataURL("image/png");
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `qrcode-${campaign.slug}.png`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  toast.success("QR code baixado!");
                }}
                className="px-5 py-2.5 bg-[#0A0A0A] text-white rounded-full text-xs font-semibold hover:bg-zinc-800 transition-colors flex items-center gap-1.5"
              >
                <Download size={13} /> {t("campaignDetail.downloadPng")}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={waOpen} onOpenChange={setWaOpen}>
          <DialogContent className="sm:max-w-md" data-testid="whatsapp-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">{en ? "WhatsApp invitation" : "Convite pelo WhatsApp"}</DialogTitle>
              <DialogDescription>{en ? "Edit the message before sending. The collection link is added at the end automatically." : "Edite a mensagem antes de enviar. O link de coleta é adicionado automaticamente ao final."}</DialogDescription>
            </DialogHeader>
            <textarea
              data-testid="whatsapp-message-input"
              value={waMessage}
              onChange={(e) => setWaMessage(e.target.value)}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 bg-[#F4F4F5] border-none rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm resize-none"
            />
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs text-zinc-600">
              <p className="font-semibold text-zinc-400 uppercase tracking-wider text-[10px] mb-1">{en ? "Preview" : "Prévia"}</p>
              {waMessage.trim()} <span className="text-[#FF4F00] break-all">{collectUrl}</span>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                data-testid="reset-whatsapp-message-btn"
                onClick={() => setWaMessage(defaultWaMessage(campaign.business_name))}
                className="px-4 py-2.5 border border-zinc-200 rounded-full text-xs font-semibold text-zinc-600 hover:border-zinc-400 transition-colors"
              >
                {en ? "Reset to default" : "Restaurar padrão"}
              </button>
              <button
                data-testid="save-whatsapp-message-btn"
                onClick={saveWaMessage}
                disabled={savingWa || !waMessage.trim()}
                className="px-4 py-2.5 border border-zinc-300 bg-white rounded-full text-xs font-semibold text-zinc-800 hover:border-zinc-500 transition-colors disabled:opacity-50"
              >
                {savingWa ? t("common.saving") : (en ? "Save message" : "Salvar mensagem")}
              </button>
              <button
                data-testid="open-whatsapp-btn"
                onClick={openWhatsApp}
                disabled={!waMessage.trim()}
                className="px-5 py-2.5 bg-[#25D366] text-white rounded-full text-xs font-semibold hover:bg-[#1EBE5A] transition-colors disabled:opacity-50"
              >
                Abrir WhatsApp
              </button>
            </div>
          </DialogContent>
        </Dialog>

        <ShareImageDialog
          open={!!shareTarget}
          onOpenChange={(o) => !o && setShareTarget(null)}
          testimonial={shareTarget}
          businessName={campaign.business_name}
        />

        <ReelsShareDialog
          open={!!reelsShare}
          onOpenChange={(o) => {
            if (!o && reelsShare?.url) URL.revokeObjectURL(reelsShare.url);
            if (!o) setReelsShare(null);
          }}
          reel={reelsShare}
          wallUrl={`${window.location.origin}/w/${campaign.slug}`}
          businessName={campaign.business_name}
        />

        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-testimonial-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading tracking-tight">{t("campaignDetail.deleteConfirm")}</AlertDialogTitle>
              <AlertDialogDescription>
                {en ? `${deleteTarget?.author_name}'s testimonial will be permanently removed, including from the wall. This can't be undone.` : `O depoimento de ${deleteTarget?.author_name} será removido permanentemente, inclusive do mural. Essa ação não pode ser desfeita.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-testimonial-btn" className="rounded-full">{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-delete-testimonial-btn" onClick={confirmDeleteTestimonial} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                {t("common.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={deleteCampaignOpen} onOpenChange={setDeleteCampaignOpen}>
          <AlertDialogContent data-testid="delete-campaign-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading tracking-tight">{t("campaignDetail.deleteCampaignConfirm")}</AlertDialogTitle>
              <AlertDialogDescription>
                {en ? `The campaign "${campaign.business_name}" and all ${testimonials?.length ?? 0} testimonials will be permanently deleted. The collection link and wall will stop working. This can't be undone.` : `A campanha "${campaign.business_name}" e todos os ${testimonials?.length ?? 0} depoimentos dela serão removidos permanentemente. O link de coleta e o mural deixarão de funcionar. Essa ação não pode ser desfeita.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-campaign-btn" className="rounded-full">{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-delete-campaign-btn" onClick={confirmDeleteCampaign} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                {en ? "Delete campaign" : "Excluir campanha"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-10">
          <p className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-1 flex items-center gap-2">
            <Palette size={14} /> {t("campaignDetail.appearance")}
          </p>
          <p className="text-sm text-zinc-500 mb-6">{t("campaignDetail.appearanceSub")}</p>
          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-semibold mb-2">{t("campaignDetail.accent")}</p>
              <div className="flex flex-wrap items-center gap-2">
                {SWATCHES.map((c) => (
                  <button
                    key={c}
                    data-testid={`widget-color-${c.replace("#", "")}`}
                    onClick={() => setWidget({ ...widget, accent: c })}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${widget.accent === c ? "ring-2 ring-offset-2 ring-zinc-800" : ""}`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
                <label className="w-8 h-8 rounded-full border border-dashed border-zinc-300 flex items-center justify-center cursor-pointer overflow-hidden relative" title="Cor personalizada">
                  <input
                    data-testid="widget-color-custom"
                    type="color"
                    value={widget.accent}
                    onChange={(e) => setWidget({ ...widget, accent: e.target.value })}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <span className="text-zinc-400 text-xs">+</span>
                </label>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">{t("campaignDetail.theme")}</p>
              <div className="flex gap-2">
                {[["light", t("campaignDetail.themeLight")], ["dark", t("campaignDetail.themeDark")]].map(([v, label]) => (
                  <button
                    key={v}
                    data-testid={`widget-theme-${v}`}
                    onClick={() => setWidget({ ...widget, theme: v })}
                    className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${widget.theme === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">{t("campaignDetail.layout")}</p>
              <div className="flex flex-wrap gap-2">
                {[["masonry", t("campaignDetail.layoutMasonry")], ["grid", t("campaignDetail.layoutGrid")], ["list", t("campaignDetail.layoutList")]].map(([v, label]) => (
                  <button
                    key={v}
                    data-testid={`widget-layout-${v}`}
                    onClick={() => setWidget({ ...widget, layout: v })}
                    className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${widget.layout === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">{t("campaignDetail.order")}</p>
              <div className="flex flex-wrap gap-2">
                {[["recent", t("campaignDetail.orderRecent")], ["top_rated", t("campaignDetail.orderTop")], ["random", t("campaignDetail.orderRandom")]].map(([v, label]) => (
                  <button
                    key={v}
                    data-testid={`widget-order-${v}`}
                    onClick={() => setWidget({ ...widget, order: v })}
                    className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${widget.order === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-400 mt-1.5">{t("campaignDetail.orderHint")}</p>
            </div>
          </div>
          <button
            data-testid="save-widget-btn"
            onClick={saveWidget}
            disabled={savingWidget}
            className="mt-6 px-6 py-2.5 bg-[#FF4F00] text-white rounded-full text-sm font-semibold hover:bg-[#E64600] transition-colors disabled:opacity-50"
          >
            {savingWidget ? t("common.saving") : t("campaignDetail.saveAppearance")}
          </button>
          <div className="mt-6">
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3">{t("campaignDetail.preview")}</p>
            <iframe
              key={previewKey}
              data-testid="widget-preview-iframe"
              src={`/w/${campaign.slug}`}
              title="Pré-visualização do mural"
              className="w-full h-80 border border-zinc-200 rounded-lg bg-[#FAFAFA]"
              loading="lazy"
            />
          </div>
        </div>

        <h2 className="font-heading font-bold text-lg mb-4">
          {en ? "Received testimonials" : "Depoimentos recebidos"} {testimonials && <span className="text-zinc-400 font-medium">({hasFilter ? `${filtered.length} ${en ? "of" : "de"} ${testimonials.length}` : testimonials.length})</span>}
        </h2>

        {testimonials && testimonials.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-6" data-testid="testimonial-filters">
            <div className="relative">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                data-testid="search-author-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("campaignDetail.searchPlaceholder")}
                className="pl-9 pr-8 py-2 w-52 bg-white border border-zinc-200 rounded-full text-xs font-medium focus:ring-2 focus:ring-[#FF4F00] focus:outline-none placeholder:text-zinc-400"
              />
              {search && (
                <button
                  data-testid="clear-search-btn"
                  onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">{en ? "Type" : "Tipo"}</span>
              <Chip testId="filter-type-all" active={filterType === "all"} onClick={() => setFilterType("all")}>{en ? "All" : "Todos"}</Chip>
              <Chip testId="filter-type-video" active={filterType === "video"} onClick={() => setFilterType("video")}>{t("record.modeVideo")}</Chip>
              <Chip testId="filter-type-audio" active={filterType === "audio"} onClick={() => setFilterType("audio")}>{t("record.modeAudio")}</Chip>
              <Chip testId="filter-type-text" active={filterType === "text"} onClick={() => setFilterType("text")}>{en ? "Text" : "Texto"}</Chip>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Status</span>
              <Chip testId="filter-status-all" active={filterStatus === "all"} onClick={() => setFilterStatus("all")}>{en ? "All" : "Todos"}</Chip>
              <Chip testId="filter-status-approved" active={filterStatus === "approved"} onClick={() => setFilterStatus("approved")}>{en ? "Live" : "No mural"}</Chip>
              <Chip testId="filter-status-hidden" active={filterStatus === "hidden"} onClick={() => setFilterStatus("hidden")}>{en ? "Hidden" : "Ocultos"}</Chip>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">{en ? "Rating" : "Nota"}</span>
              <Chip testId="filter-rating-all" active={filterRating === 0} onClick={() => setFilterRating(0)}>{en ? "All" : "Todas"}</Chip>
              {[5, 4, 3, 2, 1].map((n) => (
                <Chip key={n} testId={`filter-rating-${n}`} active={filterRating === n} onClick={() => setFilterRating(n)}>{n}★</Chip>
              ))}
            </div>
          </div>
        )}

        {testimonials === null ? (
          <div className="text-sm text-zinc-400 py-10 text-center">{t("common.loading")}</div>
        ) : testimonials.length === 0 ? (
          <div data-testid="empty-testimonials" className="border border-dashed border-zinc-300 rounded-lg py-16 flex flex-col items-center gap-3">
            <Inbox size={32} className="text-zinc-300" />
            <p className="text-zinc-500 text-sm">{t("campaignDetail.emptyInbox")}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-filtered" className="border border-dashed border-zinc-300 rounded-lg py-16 flex flex-col items-center gap-3">
            <Inbox size={32} className="text-zinc-300" />
            <p className="text-zinc-500 text-sm">{en ? "No testimonials match the filters." : "Nenhum depoimento corresponde aos filtros."}</p>
            <button
              data-testid="clear-filters-btn"
              onClick={() => { setFilterType("all"); setFilterStatus("all"); setFilterRating(0); setSearch(""); }}
              className="px-5 py-2 border border-zinc-300 rounded-full text-xs font-semibold text-zinc-600 hover:border-zinc-500 transition-colors"
            >
              Limpar filtros
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((ti) => (
              <div key={ti.id} data-testid={`testimonial-card-${ti.id}`} className="bg-white border border-zinc-200 rounded-lg p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Avatar name={ti.author_name} hasAvatar={!!ti.avatar_path} testimonialId={ti.id} />
                    <div>
                      <p className="font-semibold text-sm flex items-center gap-2">
                        {ti.author_name}
                        {lastSeen && ti.created_at > lastSeen && (
                          <span data-testid={`new-testimonial-badge-${ti.id}`} className="px-2 py-0.5 bg-[#FF4F00] text-white text-[10px] font-bold rounded-full">
                            Novo
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-zinc-500">{ti.author_role || ti.author_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`share-testimonial-btn-${ti.id}`}
                      onClick={() => setShareTarget(ti)}
                      title={en ? "Share as image" : "Compartilhar como imagem"}
                      className="w-8 h-8 rounded-full flex items-center justify-center border border-zinc-200 text-zinc-400 hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors"
                    >
                      <Share2 size={14} />
                    </button>
                    {ti.type === "video" && !ti.locked && (
                      <button
                        data-testid={`reels-download-btn-${ti.id}`}
                        onClick={() => downloadReels(ti)}
                        disabled={renderingReel === ti.id}
                        title={en ? "Download as Reels MP4 (with subtitles + logo)" : "Baixar como Reels MP4 (com legenda + logo)"}
                        className="w-8 h-8 rounded-full flex items-center justify-center border border-zinc-200 text-zinc-400 hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors disabled:opacity-40 disabled:cursor-wait"
                      >
                        {renderingReel === ti.id ? (
                          <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Film size={14} />
                        )}
                      </button>
                    )}
                    <button
                      data-testid={`pin-btn-${ti.id}`}
                      onClick={() => togglePin(ti)}
                      title={ti.pinned ? (en ? "Unpin from top" : "Desfixar do topo") : (en ? "Pin to top of wall" : "Fixar no topo do mural")}
                      className={`w-8 h-8 rounded-full flex items-center justify-center border transition-colors ${ti.pinned ? "bg-[#FF4F00] border-[#FF4F00] text-white" : "border-zinc-200 text-zinc-400 hover:border-[#FF4F00] hover:text-[#FF4F00]"}`}
                    >
                      <Pin size={14} className={ti.pinned ? "fill-white" : ""} />
                    </button>
                    <button
                      data-testid={`delete-testimonial-btn-${ti.id}`}
                      onClick={() => setDeleteTarget(ti)}
                      title={en ? "Delete testimonial" : "Excluir depoimento"}
                      className="w-8 h-8 rounded-full flex items-center justify-center border border-zinc-200 text-zinc-400 hover:border-red-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                    <span className={`text-xs font-semibold ${ti.approved ? "text-green-600" : "text-zinc-400"}`}>
                      {ti.approved ? (en ? "Live" : "No mural") : (en ? "Hidden" : "Oculto")}
                    </span>
                    <Switch
                      data-testid={`approve-switch-${ti.id}`}
                      checked={ti.approved}
                      onCheckedChange={() => toggleApprove(ti)}
                      className="data-[state=checked]:bg-[#FF4F00]"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <StarRating value={ti.rating} readOnly size={16} />
                </div>
                {ti.locked ? null : ti.highlight ? (
                  <div className="mt-3 flex items-start gap-2 flex-wrap">
                    <Sparkles size={16} className="text-[#FF4F00] mt-1 shrink-0" />
                    <p data-testid={`highlight-${ti.id}`} className="font-heading font-bold text-lg tracking-tight leading-snug">
                      {ti.highlight}
                    </p>
                    <button
                      data-testid={`regenerate-highlight-btn-${ti.id}`}
                      onClick={() => generateHighlight(ti)}
                      disabled={highlightingId === ti.id}
                      className="text-xs font-semibold text-zinc-400 hover:text-[#FF4F00] transition-colors mt-1.5 disabled:opacity-50"
                    >
                      {highlightingId === ti.id ? (en ? "Generating..." : "Gerando...") : (en ? "Regenerate" : "Regenerar")}
                    </button>
                  </div>
                ) : (ti.type === "text" ? ti.text : ti.transcript) ? (
                  <button
                    data-testid={`generate-highlight-btn-${ti.id}`}
                    onClick={() => generateHighlight(ti)}
                    disabled={highlightingId === ti.id}
                    className="mt-3 px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold text-zinc-600 flex items-center gap-1.5 hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors disabled:opacity-50"
                  >
                    <Sparkles size={13} /> {highlightingId === ti.id ? (en ? "Generating highlight..." : "Gerando destaque...") : (en ? "Generate highlight" : "Gerar frase de destaque")}
                  </button>
                ) : null}
                {ti.locked ? (
                  <div data-testid={`locked-testimonial-${ti.id}`} className="mt-4 bg-zinc-50 border border-dashed border-zinc-300 rounded-md p-5 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <Lock size={15} className="text-zinc-400" /> Depoimento bloqueado — limite do plano grátis atingido.
                    </div>
                    <Link data-testid={`unlock-upgrade-btn-${ti.id}`} to="/pricing" className="px-4 py-2 bg-[#FF4F00] text-white rounded-full text-xs font-semibold hover:bg-[#E64600] transition-colors">
                      Fazer upgrade
                    </Link>
                  </div>
                ) : ti.type === "video" ? (
                  <video
                    data-testid={`testimonial-video-${ti.id}`}
                    src={`${API}/videos/${ti.id}`}
                    poster={ti.poster_path ? `${API}/videos/${ti.id}/poster` : undefined}
                    controls
                    playsInline
                    preload="metadata"
                    className="mt-4 rounded-md bg-black max-h-72 w-full sm:max-w-md"
                  />
                ) : ti.type === "audio" ? (
                  <audio
                    data-testid={`testimonial-audio-${ti.id}`}
                    src={`${API}/videos/${ti.id}`}
                    controls
                    preload="metadata"
                    className="mt-4 w-full sm:max-w-md"
                  />
                ) : (
                  <p className="mt-4 text-sm text-zinc-700 leading-relaxed">"{ti.text}"</p>
                )}
                {!ti.locked && (ti.type === "video" || ti.type === "audio") && (
                  <div className="mt-3">
                    {editingTranscriptId === ti.id ? (
                      <div data-testid={`transcript-editor-${ti.id}`} className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-2">{t("campaignDetail.editTranscript")}</p>
                        <textarea
                          data-testid={`transcript-input-${ti.id}`}
                          value={transcriptDraft}
                          onChange={(e) => setTranscriptDraft(e.target.value)}
                          rows={4}
                          maxLength={5000}
                          className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm resize-none"
                        />
                        <div className="flex gap-2 mt-3">
                          <button
                            data-testid={`save-transcript-btn-${ti.id}`}
                            onClick={() => saveTranscript(ti)}
                            disabled={savingTranscript}
                            className="px-4 py-2 bg-[#FF4F00] text-white rounded-full text-xs font-semibold hover:bg-[#E64600] transition-colors disabled:opacity-50"
                          >
                            {savingTranscript ? t("common.saving") : t("common.save")}
                          </button>
                          <button
                            data-testid={`cancel-transcript-btn-${ti.id}`}
                            onClick={() => setEditingTranscriptId(null)}
                            className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold text-zinc-600 hover:border-zinc-400 transition-colors"
                          >
                            {t("common.cancel")}
                          </button>
                        </div>
                      </div>
                    ) : ti.transcript ? (
                      <div data-testid={`transcript-${ti.id}`} className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{t("campaignDetail.transcript")}</p>
                          <button
                            data-testid={`edit-transcript-btn-${ti.id}`}
                            onClick={() => { setEditingTranscriptId(ti.id); setTranscriptDraft(ti.transcript); }}
                            className="text-xs font-semibold text-zinc-500 flex items-center gap-1 hover:text-[#FF4F00] transition-colors"
                          >
                            <Pencil size={11} /> {t("common.edit")}
                          </button>
                        </div>
                        <p className="text-sm text-zinc-700 leading-relaxed">"{ti.transcript}"</p>
                      </div>
                    ) : ti.transcript_status === "processing" ? (
                      <p data-testid={`transcript-processing-${ti.id}`} className="text-xs text-zinc-400 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF4F00] rec-dot" /> {en ? "Transcribing automatically..." : "Transcrevendo automaticamente..."}
                      </p>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          data-testid={`transcribe-btn-${ti.id}`}
                          onClick={() => transcribe(ti)}
                          disabled={transcribingId === ti.id}
                          className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold text-zinc-600 hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors disabled:opacity-50"
                        >
                          {transcribingId === ti.id ? (en ? "Transcribing..." : "Transcrevendo...") : ti.transcript_status === "failed" ? (en ? "Try transcribing again" : "Tentar transcrever novamente") : (en ? "Generate transcript" : "Gerar transcrição")}
                        </button>
                        <button
                          data-testid={`write-transcript-btn-${ti.id}`}
                          onClick={() => { setEditingTranscriptId(ti.id); setTranscriptDraft(""); }}
                          className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold text-zinc-600 hover:border-zinc-400 transition-colors"
                        >
                          {en ? "Write manually" : "Escrever manualmente"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-zinc-400 mt-3">{new Date(ti.created_at).toLocaleDateString(en ? "en-US" : "pt-BR")}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
